import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Box,
  Typography,
  TextField,
  Button,
  Link as MuiLink,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Alert,
  CircularProgress,
  Stack,
  Container // ADD THIS IMPORT
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff,
  Microsoft, // ADD THESE IMPORTS
  Google // ADD THESE IMPORTS
} from '@mui/icons-material';
import { generateBrowserFingerprint, generateSimpleFingerprint } from '../utils/browserFingerprint';
import GoogleLoginPopup from '../components/GoogleLoginPopup';

export default function SignupPage() {
  // Initialize all form fields with empty strings to prevent uncontrolled inputs
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [socialLoading, setSocialLoading] = useState(null); // ADD SOCIAL LOADING STATE
  const navigate = useNavigate();
  const [googlePopupOpen, setGooglePopupOpen] = useState(false);
  const handleChange = (e) => {
    const { name, value } = e.target;
    // Ensure we never set undefined/null values
    setFormData(prev => ({ ...prev, [name]: value || '' }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return false;
    }
    if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  // ADD SOCIAL LOGIN HANDLERS (matching LoginPage)
  const handleMicrosoftSignup = async () => {
    setSocialLoading('microsoft');
    setError('');
    
    try {
      // Simulate Microsoft OAuth flow
      console.log('Initiating Microsoft signup...');
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock successful signup
      localStorage.setItem('token', 'mock-microsoft-token');
      navigate('/dashboard');
      
    } catch (err) {
      setError('Microsoft signup failed. Please try again.');
    } finally {
      setSocialLoading(null);
    }
  };

  const handleGoogleSignup = () => {
  setError('');
  // Open Google login popup for signup
  setGooglePopupOpen(true);
};

// Google signup success handler
const handleGoogleSignupSuccess = (token) => {
  console.log('Google signup successful with token:', token);
  // Store the token and redirect to dashboard
  localStorage.setItem('token', token);
  navigate('/dashboard');
};

// Google popup close handler
const handleGooglePopupClose = () => {
  setGooglePopupOpen(false);
};

  // handleSubmit function (existing logic)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    // Generate raw browser fingerprint for registration
    let fingerprintData = null;
    
    try {
      // Try to generate comprehensive fingerprint first
      fingerprintData = await generateBrowserFingerprint();
      if (!fingerprintData) {
        // Fallback to simple fingerprint
        fingerprintData = generateSimpleFingerprint();
      }
      console.log('Registration fingerprint data:', fingerprintData);
    } catch (fpError) {
      console.warn('Could not generate browser fingerprint:', fpError);
      // Continue without fingerprint data
    }

    setLoading(true);
    
    try {
      const registerPayload = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword
      };

      // Add raw fingerprint data to registration payload if available
      if (fingerprintData) {
        registerPayload.fingerprint_data = fingerprintData;
      }

      const response = await fetch('https://propertyearn.net/api/api/register', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify(registerPayload)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed. Please try again.');
      }

      // Store fingerprint data reference if available
      if (fingerprintData) {
        localStorage.setItem('device_fingerprint_data', JSON.stringify(fingerprintData));
      }

      // Show success message before redirecting
      setSuccess(true);
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            registrationSuccess: true,
            registeredEmail: formData.email 
          } 
        });
      }, 1500);
    } catch (err) {
      setError(err.message || 'An error occurred during registration');
      console.error('Registration error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    // UPDATE: Match LoginPage background and layout
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      display: 'flex',
      alignItems: 'center',
      py: 4
    }}>
      <Container maxWidth="sm">
        <Paper 
          elevation={8} 
          sx={{
            p: 4,
            borderRadius: 3,
            backgroundColor: 'white'
          }}
        >
          {/* UPDATE: Header matching LoginPage */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 700,
                mb: 1,
                color: 'primary.main'
              }}
            >
              Create Your Account
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'text.secondary',
                fontSize: '1.1rem'
              }}
            >
              Join PropertyEarn and start earning today
            </Typography>
          </Box>
          
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3, 
                borderRadius: 2,
                backgroundColor: 'error.light',
                color: 'error.dark'
              }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 3, 
                borderRadius: 2,
                backgroundColor: 'success.light',
                color: 'success.dark'
              }}
            >
              Registration successful! Redirecting to login...
            </Alert>
          )}

          {/* ADD: Social Signup Buttons (matching LoginPage) */}
          <Stack spacing={2} sx={{ mb: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={handleMicrosoftSignup}
              disabled={socialLoading !== null || loading || success}
              startIcon={
                socialLoading === 'microsoft' ? 
                  <CircularProgress size={20} /> : 
                  <Microsoft />
              }
              sx={{
                py: 1.5,
                borderRadius: 2,
                borderColor: 'grey.300',
                color: 'text.primary',
                fontWeight: 500,
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'primary.light',
                  color: 'primary.main'
                }
              }}
            >
              {socialLoading === 'microsoft' ? 'Signing up...' : 'Sign up with Microsoft'}
            </Button>
            
            {/* Google button with popup functionality */}
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={handleGoogleSignup} 
              disabled={socialLoading !== null || loading || success}
              startIcon={<Google />}
              sx={{
                py: 1.5,
                borderRadius: 2,
                borderColor: 'grey.300',
                color: 'text.primary',
                fontWeight: 500,
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'primary.light',
                  color: 'primary.main'
                }
              }}
            >
              Sign up with Google
            </Button>
              
          </Stack>
          
          {/* ADD: Divider matching LoginPage */}
          <Box sx={{ display: 'flex', alignItems: 'center', my: 3 }}>
            <Divider sx={{ flexGrow: 1 }} />
            <Typography 
              variant="body2" 
              sx={{ 
                px: 2, 
                color: 'text.secondary',
                fontWeight: 500
              }}
            >
              OR
            </Typography>
            <Divider sx={{ flexGrow: 1 }} />
          </Box>
          
          {/* UPDATE: Form styling to match LoginPage */}
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Full Name"
              name="name"
              variant="outlined"
              margin="normal"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={loading || success || socialLoading !== null}
              inputProps={{ maxLength: 50 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
            
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              variant="outlined"
              margin="normal"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={loading || success || socialLoading !== null}
              inputProps={{ maxLength: 100 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
            
            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              margin="normal"
              value={formData.password}
              onChange={handleChange}
              required
              disabled={loading || success || socialLoading !== null}
              inputProps={{ maxLength: 50 }}
              helperText="At least 6 characters"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={loading || success || socialLoading !== null}
                      aria-label="toggle password visibility"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
            
            <TextField
              fullWidth
              label="Confirm Password"
              name="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              margin="normal"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              disabled={loading || success || socialLoading !== null}
              inputProps={{ maxLength: 50 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
            
            {/* UPDATE: Button styling to match LoginPage */}
            <Button
              fullWidth
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disabled={loading || success || socialLoading !== null}
              sx={{ 
                mt: 3, 
                py: 1.5,
                height: 48,
                borderRadius: 2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: 4
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Create Account'
              )}
            </Button>
          </form>
          
          {/* UPDATE: Footer link styling */}
          <Typography 
            variant="body2" 
            sx={{ 
              mt: 3, 
              textAlign: 'center',
              color: 'text.secondary'
            }}
          >
            Already have an account?{' '}
            <MuiLink 
              component={Link} 
              to="/login"
              fontWeight="bold"
              underline="hover"
              sx={{ 
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              Login
            </MuiLink>
          </Typography>
        </Paper>
      </Container>

        {/* Google Login Popup for Signup */}
        <GoogleLoginPopup 
          open={googlePopupOpen}
          onClose={handleGooglePopupClose}
          onSuccess={handleGoogleSignupSuccess}
        />
    </Box>
  );
}